import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useApi } from "@/api/hooks/useApi";
import type { ServicePartnerRequest } from "@/api/types";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { QUERY_KEYS } from "@/query-keys";
import { useTranslation } from "@/translations";

const useAddServicePartner = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const showFlashToast = useFlashToast();
  const projectId = useProjectId();

  return useMutation({
    mutationFn: api.postServicepartnersV1,
    onSuccess: () => {
      showFlashToast({
        type: "success",
        title: t("model.service.action.create.notification.success"),
      });

      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.SERVICE_PARTNERS_ALL(projectId) });
    },
    onError: () => {
      showFlashToast({
        type: "error",
        title: t("model.service.action.create.notification.error"),
      });
    },
  });
};

const useUpdateServicePartner = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const showFlashToast = useFlashToast();
  const projectId = useProjectId();

  return useMutation({
    mutationFn: (payload: { id: string; data: ServicePartnerRequest }) =>
      api.putServicepartnersV1(payload.id, payload.data),
    onSuccess: async (_, payload) => {
      showFlashToast({
        type: "success",
        title: t("model.service.action.edit.notification.success"),
      });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.SERVICE_PARTNERS_ALL(projectId) });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.SERVICE_PARTNER(projectId, payload.id) });
    },
    onError: (_, payload) => {
      showFlashToast({
        type: "error",
        title: t("model.service.action.edit.notification.error"),
      });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.SERVICE_PARTNERS_ALL(projectId) });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.SERVICE_PARTNER(projectId, payload.id) });
    },
  });
};

export const servicePartnerMutations = {
  useAddServicePartner,
  useUpdateServicePartner,
};
